import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, CheckCircle } from 'lucide-react';
import { getServiceBySlug, getServices } from '../lib/cms';

const tagrenoveringFaq = [
  {
    question: "Hvor meget koster en tagrenovering typisk sammenlignet med et helt nyt tag?",
    answer: "Prisen på en tagrenovering afhænger af tagets nuværende stand, materialevalg og opgavens omfang, men det koster typisk kun en brøkdel af prisen på et helt nyt tag. Ved at renovere og tætne det eksisterende tag fremfor at udskifte det hele, kan du ofte spare mellem 50% og 70% af omkostningerne. Vi foretager altid en grundig og ærlig vurdering af dit tag, så du får den mest økonomiske og langtidsholdbare løsning til din bolig."
  },
  {
    question: "Hvor lang tid tager det at få udført en tagrenovering?",
    answer: "Varigheden af en tagrenovering varierer naturligvis efter tagets størrelse og den valgte løsning, men de fleste projekter tager mellem 3 og 7 arbejdsdage. Mindre reparationer og tætninger kan ofte klares på et par dage, mens en fuld rensning, algebehandling og efterfølgende maling eller coating tager lidt længere tid. Vi arbejder altid effektivt og struktureret for at minimere generne for dig og din familie, mens arbejdet står på."
  },
  {
    question: "Hvordan ved jeg, om mit tag skal renoveres eller helt udskiftes?",
    answer: "Hvis dit tag generelt er sundt i den underliggende konstruktion, men har overfladiske skader, mindre utætheder eller trænger til kosmetisk fornyelse, er en tagrenovering den ideelle løsning. Hvis spær, lægter eller undertag derimod er angrebet av råd, svamp eller har omfattende fugtskader, vil en komplet udskiftning være nødvendig for at sikre husets stabilitet. Toa Byg ApS tilbyder en gratis og uforpligtende gennemgang af dit tag, hvor vi rådgiver dig ærligt om den bedste vej frem."
  },
  {
    question: "Kan alle typer tagmaterialer renoveres, eller er der undtagelser?",
    answer: "De fleste gængse tagmaterialer som tegl, betonsten, eternit og tagpap kan renoveres med stor succes, så længe den bærende konstruktion er intakt. Der er dog vigtige undtagelser, især hvis der er tale om ældre eternittage med asbest, hvor der gælder særlige lovkrav og sikkerhedsregler for håndtering og rensning. Vi har stor erfaring med alle materialer og rådgiver dig altid grundigt om, hvad der er både teknisk muligt, lovligt og økonomisk forsvarligt for netop dit tag."
  },
  {
    question: "Hvor mange års ekstra levetid kan en professionel tagrenovering give mit tag?",
    answer: "En professionelt udført tagrenovering fra Toa Byg ApS kan forlænge dit tags levetid med op til 10 til 15 år, afhængigt af tagets oprindelige kvalitet og den valgte renoveringsmetode. Ved at udføre løbende vedligeholdelse, tætning af samlinger og eventuelt påføre en beskyttende tagcoating, sikrer du, at taget modstår det omskiftelige danske vejr i mange år fremover. Det er en investering, der ikke alene beskytter dit hus, men også øger ejendommens samlede værdi."
  }
];

const asbesttagFaq = [
  {
    question: "Hvad koster det at få nedtaget et asbesttag?",
    answer: "Prisen på nedtagning af et asbesttag afhænger af tagets størrelse, tilgængelighed og omfanget af det efterfølgende arbejde. Toa Byg ApS sikrer altid en gennemskuelig proces, hvor vi foretager en grundig vurdering og giver et fast, skriftligt tilbud. Vi overholder alle gældende miljøkrav og sikkerhedsregler, så du undgår uforudsete udgifter og kan føle dig helt tryg under hele forløbet."
  },
  {
    question: "Hvilke regler gælder der for nedtagning af asbesttag i dag?",
    answer: "Siden den 1. januar 2025 er det lovpligtigt, at nedtagning og sanering af asbesttage samt asbestholdige materialer udelukkende udføres af autoriserede virksomheder. Toa Byg ApS har de nødvendige certificeringer og uddannede medarbejdere til at udføre arbejdet fuldt lovligt. Vi sørger for korrekt anmeldelse til kommunen, sikker afspærring af arbejdsområdet samt miljørigtig bortskaffelse af asbestaffaldet."
  },
  {
    question: "Hvor lang tid tager det at fjerne et asbesttag og lægge nyt?",
    answer: "Selve nedtagningen og saneringen af et typisk asbesttag tager normalt mellem 2 og 4 arbejdsdage, afhængigt af tagets størrelse og kompleksitet. Derefter påbegynder vi opbygningen af den nye tagkonstruktion, hvilket typisk tager yderligere 1 til 2 uger. Vi planlægger altid arbejdet nøje og dækker midlertidigt af for vind og vejr, så din bolig forbliver tør og beskyttet under hele processen."
  },
  {
    question: "Kan jeg selv pille mit asbesttag ned eller fjerne asbestplader?",
    answer: "Nej, som følge af den nye lovgivning er det strengt forbudt for private boligejere selv at nedrive eller fjerne asbestholdige tagplader og materialer. Asbestfibre udgør en alvorlig sundhedsrisiko, hvis de frigives til luften og indåndes under nedtagningen. Ved at overlade opgaven til Toa Byg ApS sikrer du, at arbejdet udføres med professionelt sikkerhedsudstyr og efter de strengeste miljømæssige standarder."
  },
  {
    question: "Hvordan sikrer I, at der ikke spredes asbeststøv under arbejdet?",
    answer: "Vi følger en række strenge sikkerhedsprocedurer, herunder tæt afskærmning af arbejdsområdet, nænsom nedtagning uden at knuse pladerne samt løbende befugtning for at binde eventuelt støv. Vores medarbejdere bærer godkendte beskyttelsesdragter og åndedrætsværn, og vi støvsuger området med specialiserede HEPA-støvsugere. Efter endt arbejde rengør vi grundigt, så din grund og dit hjem er fuldstændig rent og asbestfrit."
  }
];

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [otherServices, setOtherServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const isAsbesttag = slug === 'asbesttag' || service?.slug?._ === 'asbesttag' || service?.id === 'asbesttag';
  const isTagrenovering = slug === 'tagrenovering' || service?.slug?._ === 'tagrenovering' || service?.id === 'tagrenovering';

  useEffect(() => {
    async function loadServiceData() {
      setLoading(true);
      try {
        const data = await getServiceBySlug(slug);
        if (!data) {
          // Hvis ydelsen ikke findes, prøv at hente alle og find ved ID eller gå tilbage
          const allServices = await getServices();
          const found = allServices.find(s => s.id === slug || s.slug?._ === slug);
          if (found) {
            setService(found);
          } else {
            navigate('/ydelser');
            return;
          }
        } else {
          setService(data);
        }

        // Hent andre ydelser til sidebaren
        const all = await getServices();
        setOtherServices(all.filter(s => (s.slug?._ || s.id) !== slug).slice(0, 4));
      } catch (err) {
        console.error('Fejl ved indlæsning af ydelsesdetaljer:', err);
        navigate('/ydelser');
      } finally {
        setLoading(false);
      }
    }
    loadServiceData();
  }, [slug, navigate]);

  // SEO Titel og Meta Description for ydelserne
  useEffect(() => {
    if (!service) return;

    let title = `${service.title} | Toa Byg ApS`;
    let description = service.description || 'Få professionelt håndværk udført af Toa Byg ApS. Kontakt os for et uforpligtende tilbud på Sjælland.';

    const serviceSlug = service?.slug?._ || service?.id || slug;

    if (isAsbesttag) {
      title = "Nedtagning af asbesttag | Sikker & professionel håndtering";
      description = "Få professionel nedtagning af asbesttag på Sjælland. Vi sikrer tryg håndtering, klar rådgivning og en sikker proces fra start til slut.";
    } else if (isTagrenovering) {
      title = "Tagrenovering Sjælland | Tryg proces | holdbare resultater";
      description = "Få professionel tagrenovering på Sjælland. Vi hjælper med tagtjek, reparation og renovering med fokus på kvalitet, tryghed og holdbare løsninger.";
    } else if (serviceSlug === 'tagarbejde') {
      title = "Tagarbejde på Sjælland | Reparation & Nyt Tag | Toa Byg ApS";
      description = "Professionelt tagarbejde i højeste kvalitet. Vi udskifter, reparerer og sikrer dit tag mod vind og vejr i Ishøj og på hele Sjælland.";
    } else if (serviceSlug === 'toemrerarbejde') {
      title = "Tømrerarbejde på Sjælland | Terrasser, Døre & Vinduer | Toa Byg ApS";
      description = "Klassisk tømrerarbejde udført med stolthed og faglig præcision. Vi bygger træterrasser, carporte, udskifter døre og vinduer samt lægger nye gulve.";
    } else if (serviceSlug === 'renovering') {
      title = "Renovering & Ombygning på Sjælland | Toa Byg ApS";
      description = "Alsidige renoveringsopgaver for private og erhverv. Vi moderniserer din bolig med respekt for den oprindelige stil og leverer til aftalt tid.";
    } else if (serviceSlug === 'entrepriseopgaver') {
      title = "Totalentreprise & Hovedentreprise på Sjælland | Toa Byg ApS";
      description = "Vi styrer dit byggeprojekt sikkert i mål. Som hovedentreprenør koordinerer vi alle faggrupper og sikrer høj kvalitet gennem hele forløbet.";
    }

    document.title = title;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);
  }, [service, isAsbesttag, isTagrenovering, slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Hero Banner */}
      <section className="relative bg-blue-950 text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={isAsbesttag 
              ? "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/b4f7e7e4-b57e-4a68-b3f8-2a4e7f0b8617/sikker-nedtagning-asbesttag-sjaelland-toa-byg.jpg" 
              : isTagrenovering
                ? "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/b4f7e7e4-b57e-4a68-b3f8-2a4e7f0b8617/professionel-tagrenovering-ishoej-sjaelland-toa-byg.jpg"
                : service.image?.[0]}
            alt={isAsbesttag 
              ? "Sikker nedtagning af asbesttag på Sjælland af Toa Byg ApS" 
              : isTagrenovering
                ? "Professionel tagrenovering på Sjælland af Toa Byg ApS"
                : service.title}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-blue-950 via-blue-950/80 to-blue-950" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/ydelser"
            className="inline-flex items-center text-sm font-semibold text-white hover:text-slate-200 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tilbage til alle ydelser
          </Link>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase max-w-4xl leading-tight">
            {isAsbesttag 
              ? "Nedtagning af asbesttag på Sjælland - trygt og ordentligt fra start til slut" 
              : isTagrenovering
                ? "Tagrenovering på Sjælland - når taget skal holde i mange år"
                : service.title}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-3xl leading-relaxed">
            {isAsbesttag 
              ? "Har du et ældre asbesttag, der skal væk, hjælper Toa Byg ApS med en tryg og velplanlagt løsning. Fra vores base i Ishøj udfører vi nedtagning af asbesttag på Sjælland for både private og erhverv, med fokus på sikkerhed, klar dialog og ordentligt håndværk." 
              : isTagrenovering
                ? "Trænger dit tag til ny styrke, bedre tæthed eller en grundig gennemgang, hjælper Toa Byg ApS med tagrenovering i Ishøj og på resten af Sjælland. Vi kombinerer solidt tømrerhåndværk med ærlig rådgivning, så du får en løsning der passer til huset og hverdagen."
                : service.description}
          </p>
        </div>
      </section>

      {/* Indhold & Sidebar */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Venstre: Indhold */}
            <div className="lg:col-span-2 space-y-12">
              {isAsbesttag ? (
                <>
                  {/* Hovedbillede */}
                  <div className="aspect-video rounded-none overflow-hidden shadow-sm">
                    <img
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/b4f7e7e4-b57e-4a68-b3f8-2a4e7f0b8617/professionel-nedtagning-asbesttag-toa-byg.jpg"
                      alt="Sikker nedtagning af asbesttag på Sjælland udført professionelt af Toa Byg ApS"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Sektion 1: Hvorfor skal et asbesttag tages ned med omtanke */}
                  <section className="space-y-6">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-wider border-b-2 border-blue-600 pb-2">
                      Hvorfor skal et asbesttag tages ned med omtanke?
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                        <p>
                          Et asbesttag kan se robust ud udefra, men materialet kræver særlig forsigtighed, når pladerne skal løsnes og fjernes. Hos <Link to="/" className="text-blue-600 hover:underline font-semibold">Toa Byg ApS</Link> håndterer vi dette med største omhu, da risikoen især opstår, hvis tagplader knækker, skæres, bores eller renses forkert, fordi asbestfibre kan frigives til omgivelserne.
                        </p>
                        <p>
                          Derfor begynder vi altid med en grundig vurdering af tagets stand, adgangsforhold og den videre byggeproces. Vi rådgiver dig ærligt om, hvad der giver bedst mening, så fjernelse af asbesttag ikke bliver en utryg opgave, du står alene med.
                        </p>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 border-l-4 border-l-blue-600 p-6 flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-3">
                          <i className="fa-solid fa-shield-halved text-blue-600 text-2xl" />
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs sm:text-sm">Sikkerhed & Lovgivning</h4>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                          Asbestfibre kræver professionel håndtering. Vi følger alle gældende miljø- og arbejdsmiljøregler til punkt og prikke, så du kan føle dig helt tryg under hele forløbet.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Sektion 2: Nedtagning af asbesttag i Ishøj og på hele Sjælland */}
                  <section className="space-y-6">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-wider border-b-2 border-blue-600 pb-2">
                      Nedtagning af asbesttag i Ishøj og på hele Sjælland
                    </h2>
                    <div className="space-y-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                      <p>
                        Står du i Ishøj, Greve, Køge, Roskilde eller et andet sted på Sjælland, kan vi hjælpe med en løsning, der passer til din ejendom. Mange søger efter nedtagning af asbesttag in Ishøj eller nedtagning af asbesttag på Sjælland, fordi de ønsker lokal faglighed og en fast kontaktperson.
                      </p>
                      <p>
                        Som tømrer og byggefirma har vi blik for mere end selve nedtagningen. Vi ser på tagkonstruktionen, det kommende tagarbejde og de detaljer, der skal spille sammen bagefter. Det gør fjernelse af asbesttag på Sjælland mere effektivt og mere overskueligt for dig.
                      </p>
                    </div>

                    {/* De 3 punkter som kasser */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                      <div className="bg-white p-6 border border-slate-100 shadow-sm flex flex-col justify-between hover:border-blue-200 transition-all">
                        <div>
                          <div className="w-12 h-12 bg-blue-50 rounded-none flex items-center justify-center mb-4">
                            <i className="fa-solid fa-clipboard-list text-blue-600 text-xl" />
                          </div>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs sm:text-sm mb-2">Grundig planlægning</h4>
                          <p className="text-xs text-slate-500 leading-relaxed">
                            Vi gennemgår tagtype, tagflade, adgangsveje og plads omkring bygningen, så opgaven kan planlægges realistisk og med færrest mulige overraskelser.
                          </p>
                        </div>
                      </div>

                      <div className="bg-white p-6 border border-slate-100 shadow-sm flex flex-col justify-between hover:border-blue-200 transition-all">
                        <div>
                          <div className="w-12 h-12 bg-blue-50 rounded-none flex items-center justify-center mb-4">
                            <i className="fa-solid fa-shield-halved text-blue-600 text-xl" />
                          </div>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs sm:text-sm mb-2">Sikker håndtering</h4>
                          <p className="text-xs text-slate-500 leading-relaxed">
                            Vi hjælper med sikker tilrettelæggelse, afskærmning, håndtering og bortskaffelse, så arbejdet foregår ordentligt og efter gældende krav.
                          </p>
                        </div>
                      </div>

                      <div className="bg-white p-6 border border-slate-100 shadow-sm flex flex-col justify-between hover:border-blue-200 transition-all">
                        <div>
                          <div className="w-12 h-12 bg-blue-50 rounded-none flex items-center justify-center mb-4">
                            <i className="fa-solid fa-house-chimney text-blue-600 text-xl" />
                          </div>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs sm:text-sm mb-2">Samlet proces</h4>
                          <p className="text-xs text-slate-500 leading-relaxed">
                            Vi koordinerer den videre proces, hvis dit gamle asbesttag skal erstattes af en ny tagløsning med bedre holdbarhed.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Sektion 3: Processen: Ro, kontrol og grundigt forarbejde */}
                  <section className="bg-blue-950 text-white p-6 md:p-10 rounded-none space-y-8">
                    <div className="text-center max-w-2xl mx-auto">
                      <span className="text-blue-400 text-xs font-bold uppercase tracking-widest">Vores tilgang</span>
                      <h3 className="text-xl sm:text-2xl font-extrabold uppercase tracking-wider mt-2">Ro, kontrol og grundigt forarbejde</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-blue-900/40 p-6 border border-blue-800/50">
                        <div className="flex items-center gap-3 mb-4">
                          <i className="fa-solid fa-magnifying-glass text-blue-400 text-lg" />
                          <h4 className="font-bold uppercase tracking-wider text-xs sm:text-sm">Første trin: Grundigt tagtjek</h4>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                          Et godt resultat starter med, at vi lytter til dine planer og ser nærmere på taget. Her vurderer vi blandt andet omfang, underlag, mulige skader og behovet for efterfølgende <Link to="/ydelser/tagrenovering" className="text-white hover:underline font-bold">tagrenovering</Link>, så du får en samlet vurdering og ikke kun et hurtigt overslag.
                        </p>
                      </div>

                      <div className="bg-blue-900/40 p-6 border border-blue-800/50">
                        <div className="flex items-center gap-3 mb-4">
                          <i className="fa-solid fa-clock text-blue-400 text-lg" />
                          <h4 className="font-bold uppercase tracking-wider text-xs sm:text-sm">kontrol under nedtagning</h4>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                          Ved fjernelse af asbesttag er tempo ikke det vigtigste. Det vigtigste er en kontrolleret proces, hvor plader håndteres nænsomt, området holdes ryddeligt og alle aftaler er tydelige. Når der kræves særlige fagfolk, sørger vi for, at de rette kompetencer bliver inddraget.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Sektion 4: Hvad påvirker prisen på nedtagning af asbesttag */}
                  <section className="space-y-6">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-wider border-b-2 border-blue-600 pb-2">
                      Hvad påvirker prisen på nedtagning af asbesttag?
                    </h2>
                    <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                      Prisen afhænger af flere praktiske forhold, og derfor giver det sjældent mening med en standardpris uden at have set opgaven. Tagets størrelse, hældning, adgang, stillads, mængden af affald og ønsker til nyt tag har betydning for den samlede løsning.
                    </p>

                    <div className="space-y-4 mt-6">
                      <div className="bg-white p-5 border-l-4 border-blue-600 shadow-sm flex gap-4 items-start">
                        <div className="w-8 h-8 bg-blue-50 rounded-none flex items-center justify-center shrink-0 mt-0.5">
                          <i className="fa-solid fa-up-down-left-right text-blue-600 text-sm" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs mb-1">Tilgængelighed & Størrelse</h4>
                          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                            Et lille og let tilgængeligt tag kan som regel planlægges enklere end en stor tagflade med svær adgang eller mange gennembrydninger.
                          </p>
                        </div>
                      </div>

                      <div className="bg-white p-5 border-l-4 border-blue-600 shadow-sm flex gap-4 items-start">
                        <div className="w-8 h-8 bg-blue-50 rounded-none flex items-center justify-center shrink-0 mt-0.5">
                          <i className="fa-solid fa-arrows-spin text-blue-600 text-sm" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs mb-1">Samlet forløb</h4>
                          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                            Hvis du ønsker udskiftning af asbesttag i samme forløb, kan vi tænke nedtagning, tømrerarbejde og ny tagløsning sammen fra begyndelsen.
                          </p>
                        </div>
                      </div>

                      <div className="bg-white p-5 border-l-4 border-blue-600 shadow-sm flex gap-4 items-start">
                        <div className="w-8 h-8 bg-blue-50 rounded-none flex items-center justify-center shrink-0 mt-0.5">
                          <i className="fa-solid fa-magnifying-glass-chart text-blue-600 text-sm" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs mb-1">Skjulte skader</h4>
                          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                            Ved ældre tage kan der være skjulte skader i lægter, undertag eller konstruktion, som først bliver tydelige, når de gamle plader fjernes.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Sektion 5: Fra gammelt asbesttag til et sundere tagprojekt */}
                  <section className="space-y-6">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-wider border-b-2 border-blue-600 pb-2">
                      Fra gammelt asbesttag til et sundere tagprojekt
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                      <div className="space-y-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                        <p>
                          Mange kunder vælger at bruge nedtagningen som start på en større forbedring af boligen eller erhvervsbygningen. Når det gamle asbesttag er fjernet, kan vi hjælpe med tagarbejde, tømrerarbejde og løsninger, der passer til husets stil og dit budget.
                        </p>
                        <p>
                          Vi lægger vægt på ærlig rådgivning, så du ved, hvad der er nødvendigt nu, og hvad der med fordel kan vente. Det giver dig et bedre beslutningsgrundlag, uanset om du ønsker en enkel løsning eller en samlet entreprise med flere fagområder.
                        </p>
                      </div>
                      <div className="aspect-video rounded-none overflow-hidden shadow-sm">
                        <img
                          src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/b4f7e7e4-b57e-4a68-b3f8-2a4e7f0b8617/nyt-tag-efter-asbestsanering-toa-byg.jpg"
                          alt="Nyt tag monteret af tømrerfirmaet Toa Byg ApS efter sikker asbestsanering"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </section>

                  {/* Sektion 6: Få en tryg vurdering af dit asbesttag (CTA) */}
                  <section className="bg-gradient-to-r from-blue-900 to-blue-950 text-white p-6 md:p-10 rounded-none shadow-sm relative overflow-hidden">
                    <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-blue-800/20 rounded-full" />
                    <div className="relative z-10 space-y-6 max-w-2xl">
                      <h3 className="text-xl sm:text-2xl font-extrabold uppercase tracking-wider">Få en tryg vurdering af dit asbesttag</h3>
                      <p className="text-slate-300 leading-relaxed text-xs sm:text-sm">
                        Overvejer du nedtagning af asbesttag eller fjernelse af asbesttag, er du velkommen til at kontakte Toa Byg ApS. Vi tager os tid til at forstå opgaven, forklare processen og give dig et gennemskueligt tilbud, så du kan komme sikkert videre med dit tagprojekt.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 pt-2">
                        <a
                          href="tel:+4540942420"
                          className="inline-flex items-center justify-center gap-2 bg-white text-blue-950 px-6 py-3 font-bold uppercase tracking-wider text-xs hover:bg-slate-100 transition-all shadow-sm"
                        >
                          <i className="fa-solid fa-phone" />
                          Ring: +45 40 94 24 20
                        </a>
                        <Link
                          to="/kontakt"
                          className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 font-bold uppercase tracking-wider text-xs hover:bg-blue-700 transition-all shadow-sm"
                        >
                          Få et uforpligtende tilbud
                        </Link>
                      </div>
                    </div>
                  </section>

                  {/* Sektion 7: FAQ om Nedtagning af Asbesttag */}
                  <section className="space-y-6 mt-12">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-wider border-b-2 border-blue-600 pb-2">
                      Ofte stillede spørgsmål om asbesttag
                    </h2>
                    <div className="space-y-4">
                      {asbesttagFaq.map((item, index) => {
                        const isOpen = openFaqIndex === index;
                        return (
                          <div key={index} className="border border-slate-200 bg-white transition-all duration-200">
                            <button
                              onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                              className="w-full flex justify-between items-center p-4 text-left font-bold text-slate-900 uppercase tracking-wider text-xs sm:text-sm hover:bg-slate-50 transition-colors cursor-pointer"
                              aria-expanded={isOpen}
                            >
                              <span>{item.question}</span>
                              <i className={`fa-solid ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'} text-blue-600 transition-transform duration-200`} />
                            </button>
                            <div
                              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                                isOpen ? 'max-h-96 border-t border-slate-100' : 'max-h-0'
                              }`}
                            >
                              <p className="p-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                                {item.answer}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                </>
              ) : isTagrenovering ? (
                <>
                  {/* Hovedbillede */}
                  <div className="aspect-video rounded-none overflow-hidden shadow-sm">
                    <img
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/b4f7e7e4-b57e-4a68-b3f8-2a4e7f0b8617/image00004.jpeg"
                      alt="Professionel tagrenovering i Ishøj og på Sjælland udført af Toa Byg ApS"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Sektion 1: Hvornår er tagrenovering det klogeste valg */}
                  <section className="space-y-6">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-wider border-b-2 border-blue-600 pb-2">
                      Hvornår er tagrenovering det klogeste valg
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                        <p>
                          Et slidt tag afslører sig ofte med løse plader, revner, fugtspor, mørke skjolder eller træværk der ikke længere ser sundt ud. En renovering af tag handler om at stoppe problemerne i tide, før de udvikler sig til skader på konstruktion, isolering eller loftrum.
                        </p>
                        <p>
                          Vi starter med at se på tagets stand, alder, materialer og de steder hvor vand og vind typisk presser sig ind. Det giver dig et klart billede af, om en reparation er nok, om taget bør renoveres mere omfattende, eller om et nyt tag er den mest fornuftige vej.
                        </p>
                      </div>
                      <div className="bg-slate-100/60 border border-slate-200 p-6 flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-4">
                          <i className="fa-solid fa-triangle-exclamation text-blue-600 text-xl" />
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs sm:text-sm">Typiske tegn på slid</h4>
                        </div>
                        <ul className="space-y-2 text-xs sm:text-sm text-slate-700">
                          <li className="flex items-center gap-2">
                            <i className="fa-solid fa-circle-chevron-right text-blue-600 text-xs shrink-0" />
                            <span>Løse tagplader eller revner</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="fa-solid fa-circle-chevron-right text-blue-600 text-xs shrink-0" />
                            <span>Fugtspor og mørke skjolder</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="fa-solid fa-circle-chevron-right text-blue-600 text-xs shrink-0" />
                            <span>Træværk der ikke ser sundt ud</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <i className="fa-solid fa-circle-chevron-right text-blue-600 text-xs shrink-0" />
                            <span>Utætheder ved inddækninger</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Sektion 2: Renovering af tag på Sjælland, fra første tagtjek til færdigt resultat */}
                  <section className="space-y-6">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-wider border-b-2 border-blue-600 pb-2">
                      Renovering af tag på Sjælland, fra første tagtjek til færdigt resultat
                    </h2>
                    <div className="space-y-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                      <p>
                        Som tømrer og byggefirma i Ishøj rykker vi ud til både private og erhverv, der ønsker tagrenovering på Sjælland med fokus på kvalitet og ordentlig dialog. Du får en fast kontakt, tydelige aftaler og håndværkere der tager ansvar for hele processen.
                      </p>
                      <p>
                        Vi udfører tagarbejde på Sjælland for kunder der vil have et tag, som ikke bare ser pænt ud, men også fungerer i praksis. Derfor kigger vi på samspillet mellem tagflade, inddækninger, ventilation, trædele og afslutninger, inden vi anbefaler en løsning.
                      </p>
                    </div>
                  </section>

                  {/* Sektion 3: Sådan foregår arbejdet hos os */}
                  <section className="space-y-6">
                    <div className="bg-blue-50 border border-blue-100 p-6 md:p-8">
                      <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 uppercase tracking-wider mb-3">
                        Sådan foregår arbejdet hos os
                      </h3>
                      <p className="text-slate-600 leading-relaxed text-sm sm:text-base mb-6">
                        Når vi tager fat på en renovering af tag på Sjælland, sørger vi for at gøre forløbet overskueligt. Du får forklaret hvad vi ser, hvad der bør prioriteres, og hvordan arbejdet kan udføres med mindst muligt besvær for dig og dem der bruger bygningen.
                      </p>
                      
                      {/* 4 trin som proceslinje */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
                        <div className="bg-white p-5 border border-slate-100 shadow-sm relative">
                          <span className="absolute top-3 right-3 text-2xl font-black text-blue-100">01</span>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs mb-2 pr-6">Besigtigelse</h4>
                          <p className="text-xs text-slate-500 leading-relaxed">
                            Vi besigtiger taget og taler om ønsker, stand og budget.
                          </p>
                        </div>

                        <div className="bg-white p-5 border border-slate-100 shadow-sm relative">
                          <span className="absolute top-3 right-3 text-2xl font-black text-blue-100">02</span>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs mb-2 pr-6">Vurdering</h4>
                          <p className="text-xs text-slate-500 leading-relaxed">
                            Vi vurderer om reparation, delvis tagrenovering eller større udskiftning giver mest mening.
                          </p>
                        </div>

                        <div className="bg-white p-5 border border-slate-100 shadow-sm relative">
                          <span className="absolute top-3 right-3 text-2xl font-black text-blue-100">03</span>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs mb-2 pr-6">Planlægning</h4>
                          <p className="text-xs text-slate-500 leading-relaxed">
                            Vi planlægger arbejdet, så tidsforbrug, adgang og materialer er afklaret.
                          </p>
                        </div>

                        <div className="bg-white p-5 border border-slate-100 shadow-sm relative">
                          <span className="absolute top-3 right-3 text-2xl font-black text-blue-100">04</span>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs mb-2 pr-6">Udførelse</h4>
                          <p className="text-xs text-slate-500 leading-relaxed">
                            Vi udfører opgaven med fokus på tæthed, finish og oprydning.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Sektion 4: Hvad kan en tagrenovering omfatte */}
                  <section className="space-y-6">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-wider border-b-2 border-blue-600 pb-2">
                      Hvad kan en tagrenovering omfatte
                    </h2>
                    <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                      Ingen tage er ens, og en god tagrenovering skal tilpasses bygningens alder, tagtype og de skader der allerede er synlige. Hos <Link to="/" className="text-blue-600 hover:text-blue-800 hover:underline font-medium">Toa Byg ApS</Link> kan vi hjælpe med både mindre tagreparationer og større forløb, hvor flere dele af taget bliver gennemgået.
                    </p>

                    {/* 2x2 grid med ikoner */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div className="bg-white p-5 border-l-4 border-blue-600 shadow-sm flex gap-4 items-start">
                        <div className="w-10 h-10 bg-blue-50 rounded-none flex items-center justify-center shrink-0">
                          <i className="fa-solid fa-screwdriver-wrench text-blue-600 text-base" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs mb-1">Reparation & Udskiftning</h4>
                          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                            Reparation eller udskiftning af beskadigede tagdele.
                          </p>
                        </div>
                      </div>

                      <div className="bg-white p-5 border-l-4 border-blue-600 shadow-sm flex gap-4 items-start">
                        <div className="w-10 h-10 bg-blue-50 rounded-none flex items-center justify-center shrink-0">
                          <i className="fa-solid fa-magnifying-glass text-blue-600 text-base" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs mb-1">Kontrol af Træværk</h4>
                          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                            Kontrol af undertag, lægter, stern og synlige trædele.
                          </p>
                        </div>
                      </div>

                      <div className="bg-white p-5 border-l-4 border-blue-600 shadow-sm flex gap-4 items-start">
                        <div className="w-10 h-10 bg-blue-50 rounded-none flex items-center justify-center shrink-0">
                          <i className="fa-solid fa-wind text-blue-600 text-base" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs mb-1">Tæthed & Ventilation</h4>
                          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                            Forbedring af tæthed, ventilation og afslutninger omkring gennemføringer.
                          </p>
                        </div>
                      </div>

                      <div className="bg-white p-5 border-l-4 border-blue-600 shadow-sm flex gap-4 items-start">
                        <div className="w-10 h-10 bg-blue-50 rounded-none flex items-center justify-center shrink-0">
                          <i className="fa-solid fa-compass-drafting text-blue-600 text-base" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 uppercase tracking-wider text-xs mb-1">Planlægning af Nyt Tag</h4>
                          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                            Planlægning af nyt tag, hvis renovering ikke er den bedste løsning.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Sektion 5: Hvad bør du vide om ældre tage og asbest */}
                  <section className="space-y-6">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-wider border-b-2 border-blue-600 pb-2">
                      Hvad bør du vide om ældre tage og asbest
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="md:col-span-2 space-y-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                        <p>
                          Har du et ældre eternittag, eller er der mistanke om <Link to="/ydelser/asbesttag" className="text-blue-600 hover:text-blue-800 hover:underline font-medium">asbest</Link>, kræver arbejdet særlig omtanke. Vi rådgiver om næste skridt og sørger for, at renovering af tag bliver vurderet forsvarligt, så sikkerhed, regler og den rigtige løsning bliver tænkt ind fra start.
                        </p>
                        <p>
                          Det er ikke altid klogt at lappe videre på et tag, der har nået sin levetid. I nogle tilfælde er en målrettet tagrenovering nok, mentre andre tage bør udskiftes. Vores opgave er at give dig en ærlig vurdering, før du bruger penge på noget der ikke holder.
                        </p>
                      </div>
                      <div className="bg-slate-900 text-white p-6 flex flex-col justify-center border-t-4 border-blue-600">
                        <div className="flex items-center gap-3 mb-3">
                          <i className="fa-solid fa-shield-halved text-blue-400 text-xl" />
                          <h4 className="font-bold uppercase tracking-wider text-xs sm:text-sm">Asbest & Sikkerhed</h4>
                        </div>
                        <p className="text-xs text-slate-300 leading-relaxed">
                          Vi rådgiver grundigt om regler og sikkerhed ved ældre tage, så du undgår ubehagelige overraskelser og sikrer en sund ejendom.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Sektion 6: Få tagrenovering i Ishøj med en løsning der holder (CTA) */}
                  <section className="bg-gradient-to-r from-blue-900 to-blue-950 text-white p-6 md:p-10 rounded-none shadow-sm relative overflow-hidden">
                    <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-blue-800/20 rounded-full" />
                    <div className="relative z-10 space-y-6 max-w-2xl">
                      <h3 className="text-xl sm:text-2xl font-extrabold uppercase tracking-wider">Få tagrenovering i Ishøj med en løsning der holder</h3>
                      <p className="text-slate-300 leading-relaxed text-xs sm:text-sm">
                        Overvejer du tagrenovering i Ishøj, eller søger du en lokal håndværker til tagrenovering på Sjælland, er du velkommen til at kontakte Toa Byg ApS. Vi tager en uforpligtende snak om taget, dine ønsker og den løsning der giver mest værdi på lang sigt.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 pt-2">
                        <a
                          href="tel:+4540942420"
                          className="inline-flex items-center justify-center gap-2 bg-white text-blue-950 px-6 py-3 font-bold uppercase tracking-wider text-xs hover:bg-slate-100 transition-all shadow-sm"
                        >
                          <i className="fa-solid fa-phone" />
                          Ring: +45 40 94 24 20
                        </a>
                        <Link
                          to="/kontakt"
                          className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 font-bold uppercase tracking-wider text-xs hover:bg-blue-700 transition-all shadow-sm"
                        >
                          Få et uforpligtende tilbud
                        </Link>
                      </div>
                    </div>
                  </section>

                  {/* Sektion 7: FAQ om Tagrenovering */}
                  <section className="space-y-6 mt-12">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-wider border-b-2 border-blue-600 pb-2">
                      Ofte stillede spørgsmål om tagrenovering
                    </h2>
                    <div className="space-y-4">
                      {tagrenoveringFaq.map((item, index) => {
                        const isOpen = openFaqIndex === index;
                        return (
                          <div key={index} className="border border-slate-200 bg-white transition-all duration-200">
                            <button
                              onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                              className="w-full flex justify-between items-center p-4 text-left font-bold text-slate-900 uppercase tracking-wider text-xs sm:text-sm hover:bg-slate-50 transition-colors cursor-pointer"
                              aria-expanded={isOpen}
                            >
                              <span>{item.question}</span>
                              <i className={`fa-solid ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'} text-blue-600 transition-transform duration-200`} />
                            </button>
                            <div
                              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                                isOpen ? 'max-h-96 border-t border-slate-100' : 'max-h-0'
                              }`}
                            >
                              <p className="p-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                                {item.answer}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                </>
              ) : (
                <>
                  {/* Billede */}
                  <div className="aspect-w-16 aspect-h-9 rounded-none overflow-hidden mb-8">
                    <img
                      src={service.image?.[0]}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Rich Text Rendering */}
                  <div
                    className="space-y-6 [&_h3]:text-2xl [&_h3]:font-extrabold [&_h3]:text-slate-900 [&_h3]:uppercase [&_h3]:tracking-wider [&_h3]:mt-8 [&_h3]:mb-4 [&_h4]:text-xl [&_h4]:font-bold [&_h4]:text-slate-900 [&_h4]:uppercase [&_h4]:tracking-wider [&_h4]:mt-6 [&_h4]:mb-3 [&_p]:text-base [&_p]:text-slate-600 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_li]:text-slate-600 [&_strong]:text-slate-900"
                    dangerouslySetInnerHTML={{ __html: service.content }}
                  />
                </>
              )}
            </div>

            {/* Højre: Sidebar */}
            <div className="space-y-8">
              {/* Andre Ydelser */}
              <div className="bg-white rounded-none p-6 shadow-sm border border-slate-100">
                <h3 className="text-lg font-bold text-slate-900 mb-4 pb-3 border-b border-slate-100 uppercase">
                  Andre ydelser
                </h3>
                <ul className="space-y-3">
                  {otherServices.map((s) => (
                    <li key={s.id}>
                      <Link
                        to={`/ydelser/${s.slug?._ || s.id}`}
                        className="flex items-center justify-between p-3 rounded-none hover:bg-blue-50 text-slate-700 hover:text-blue-600 font-medium transition-all text-sm uppercase tracking-wider"
                      >
                        <span>{s.title}</span>
                        <span className="text-blue-600">→</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Hurtig Kontakt */}
              <div className="bg-blue-900 text-white rounded-none p-6 shadow-sm relative overflow-hidden">
                <div className="absolute -top-12 -right-12 w-24 h-24 bg-blue-800 rounded-full opacity-30" />
                <h3 className="text-xl font-bold mb-3 uppercase">Få et uforpligtende tilbud</h3>
                <p className="text-blue-100 text-sm mb-6 leading-relaxed">
                  Har du brug for rådgivning eller et prisoverslag på {service.title.toLowerCase()}? Ring eller skriv til os i dag.
                </p>
                <div className="space-y-4">
                  <a
                    href="tel:+4540942420"
                    className="flex items-center gap-3 hover:text-blue-200 transition-colors text-sm font-semibold uppercase tracking-wider"
                  >
                    <Phone className="h-5 w-5 text-white shrink-0" />
                    <span>+45 40 94 24 20</span>
                  </a>
                  <a
                    href="mailto:toabyg1@gmail.com"
                    className="flex items-center gap-3 hover:text-blue-200 transition-colors text-sm font-semibold uppercase tracking-wider"
                  >
                    <Mail className="h-5 w-5 text-white shrink-0" />
                    <span>toabyg1@gmail.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
